"use client"

import { useEffect } from "react"
import type { Locale } from "@/i18n/routing"

type ModelContext = {
  registerTool: (tool: {
    name: string
    description: string
    inputSchema: { type: "object"; properties: Record<string, never>; additionalProperties: false }
    annotations: { readOnlyHint: boolean; consequentialHint: boolean }
    execute: () => Promise<string>
  }, options: { signal: AbortSignal }) => Promise<void> | void
}

// Progressive enhancement: browsers without native WebMCP keep the regular UI.
// This tool exposes only published business information, never customer records.
export function SiteAgentTools({ locale }: { locale: Locale }) {
  useEffect(() => {
    const context = (document as Document & { modelContext?: ModelContext }).modelContext
    if (!context?.registerTool) return
    const controller = new AbortController()
    const origin = "https://danarolimo.com"
    const prefix = locale === "es" ? "/es" : ""
    void (async () => { await context.registerTool({
      name: "get_danaro_information",
      description: locale === "es"
        ? "Consulta los datos públicos de Danaro y enlaces a la flota, reservas y asistencia. No consulta ni modifica reservas, cuentas o pagos."
        : "Read Danaro's public business information and links to fleet, booking and support. Does not access or change reservations, accounts or payments.",
      inputSchema: { type: "object", properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true, consequentialHint: false },
      execute: async () => JSON.stringify({
        name: "Danaro Limousines", language: locale,
        telephone: "+1-201-939-7077", email: "info@danarolimo.com",
        address: { streetAddress: "321 10th Street", addressLocality: "Carlstadt", addressRegion: "NJ", postalCode: "07072", addressCountry: "US" },
        timeZone: "America/New_York", currency: "USD",
        links: { fleet: origin + prefix + "/fleet/", booking: origin + prefix + "/reserve/", contact: origin + prefix + "/contact-us/", terms: origin + prefix + "/booking-terms/" },
        bookingGuidance: locale === "es"
          ? "Sigue el enlace de reservas para acceder al servicio activo. Los vehículos publicados no garantizan disponibilidad ni precio. Una solicitud no es una reserva confirmada ni un pago."
          : "Follow the booking entry link for the currently active booking service. Vehicle listings do not guarantee availability or price. A submitted inquiry is not a confirmed reservation or a payment.",
      }),
    }, { signal: controller.signal }) })().catch(error => {
      if (!controller.signal.aborted && error?.name !== "NotAllowedError") console.error("Danaro WebMCP registration failed", error)
    })
    return () => controller.abort()
  }, [locale])
  return null
}
